import React, { useState } from "react";
import NotificationModal from "./NotificationModal";
import DateInput from "./shared/DateInput";
import SelectInput from "./shared/SelectInput";
import { GetReportsDownload, GetReportsData } from "../apis/get";
import { getEstDateTime } from "../../utilities/util";

const DownloadReports: React.FC = () => {
  const [fromDate, setFromDate] = useState(
    getEstDateTime().toISOString().slice(0, 10)
  );
  const [toDate, setToDate] = useState(
    getEstDateTime().toISOString().slice(0, 10)
  );
  const [type, setType] = useState("Schedule");
  const [loading, setLoading] = useState(false);
  const [showNotice, setShowNotice] = useState({ state: false, message: "" });
  const [reportData, setReportData] = useState<any[]>([]);

  const handleSearch = async () => {
    if (!fromDate || !toDate) {
      setShowNotice({ state: true, message: "Both dates are required." });
      return;
    }

    try {
      setLoading(true);
      const query = new URLSearchParams({
        fromDate,
        toDate,
        type,
        export: "false",
      });
      const res = await GetReportsData(query);
      console.log("RES", res);
      if (res?.status) {
        if (res.data && res.data.length > 0) {
          setReportData(res.data);
        } else {
          setReportData([]);
          setShowNotice({
            state: true,
            message: "No data found for selected dates.",
          });
        }
      } else {
        setShowNotice({ state: true, message: res.message ?? res });
      }
    } catch (error) {
      console.error(error);
      setShowNotice({ state: true, message: "Error fetching data." });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!fromDate || !toDate) return;

    try {
      const query = new URLSearchParams({
        fromDate,
        toDate,
        type,
        export: "true",
      });
      const response = await GetReportsDownload(query);

      const contentDisposition = response?.headers?.["content-disposition"];
      let filename = "download.xlsx";
      if (contentDisposition?.includes("filename=")) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) filename = match[1];
      }

      const blob = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Download error:", error);
      setShowNotice({ state: true, message: "Download failed. Try again." });
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto p-6 bg-black bg-opacity-40 backdrop-blur rounded-2xl shadow-2xl text-white mt-8">
      <h2 className="text-xl font-bold text-yellow-300 flex items-center gap-2">
        <i className="fas fa-file-download text-yellow-400"></i> Download
        Reports
      </h2>

      <div className="grid md:grid-cols-3 gap-4">
        <DateInput
          label="From Date"
          name="fromDate"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          required
        />
        <DateInput
          label="To Date"
          name="toDate"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          required
        />
        <SelectInput
          label="Report Type"
          name="type"
          value={type}
          onChange={(e) => setType(e.target.value)}
          required
          options={[
            { label: "Schedule", value: "Schedule" },
            { label: "Tardiness", value: "Tardiness" },
          ]}
        />
      </div>

      <div className="flex justify-end gap-4">
        <button
          onClick={handleSearch}
          className="bg-blue-500 hover:bg-blue-600 text-black font-bold px-6 py-2 rounded-lg shadow transition"
          disabled={loading}
        >
          {loading ? "Searching..." : "Search"}
        </button>

        <button
          onClick={handleDownload}
          className={`bg-green-500 hover:bg-green-600 text-black font-bold px-6 py-2 rounded-lg shadow transition ${
            !reportData.length ? "opacity-50 cursor-not-allowed" : ""
          }`}
          disabled={!reportData.length}
        >
          {loading ? "Preparing..." : "Download Excel"}
        </button>
      </div>

      {reportData.length > 0 && (
        <div className="overflow-auto max-h-[500px] border rounded-md bg-white text-black mt-6">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                {Object.keys(reportData[0]).map((key) => (
                  <th
                    key={key}
                    className="px-4 py-2 text-left border-b font-semibold"
                  >
                    {key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reportData.map((item, idx) => (
                <tr key={idx} className="hover:bg-yellow-50">
                  {Object.values(item).map((val, i) => (
                    <td
                      key={i}
                      className="px-4 py-2 border-b whitespace-nowrap"
                    >
                      {typeof val === "string" || typeof val === "number"
                        ? val
                        : JSON.stringify(val)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <NotificationModal
        show={showNotice.state}
        onClose={() => setShowNotice({ state: false, message: "" })}
        message={showNotice.message}
      />
    </div>
  );
};

export default DownloadReports;
